import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AppFlowEvent } from '../game/state/appFlowFsm'
import { store } from '../stores/store'
import { retryFailedThreeDAssets, useThreeDAssetPreload } from '../game/preloadAssets'
import Game from '../components/scene/Game'
import { retryFailedKtx2Textures, useKtx2PreloadStatus } from '../game/ktx2Textures'
import SplashScreen from '../components/screens/SplashScreen'
import StartScreen from '../components/screens/StartScreen'
import IntroScreen from '../components/screens/IntroScreen'
import ConfiguratorScreen from '../components/screens/ConfiguratorScreen'
import CountdownScreen from '../components/screens/CountdownScreen'
import CardLandingScreen from '../components/screens/CardLandingScreen'
import SettingsScreen from '../components/screens/SettingsScreen'
import InactivityGuard from '../components/screens/InactivityGuard'
import ResetTransitionScreen from '../components/screens/ResetTransitionScreen'
import { useCardIdQuery } from '../hooks/useCardIdQuery'
import { useCharacterIdQuery } from '../hooks/useCharacterIdQuery'
import { useSvidQuery } from '../hooks/useSvidQuery'
import { applySteleDeviceAttribute } from '../utils/svidQuery'
import { useSoundSystem } from '../hooks/useSound'
import { useIntroFlowMusic } from '../hooks/useIntroFlowMusic'
import { useDecodedCardSharePayload } from '../hooks/useDecodedCardSharePayload'
import { useDecodedCharacterSharePayload } from '../hooks/useDecodedCharacterSharePayload'
import { AnimatePresence, motion } from 'motion/react'
import { useSoundPreloadStatus } from '../utils/soundPlayer'
import { GetDebuggingDocument } from '../lib/graphql/generated/graphql'
import { useIsTouchDevice, useSteleBreakpoint } from '../hooks/useBreakpoint'
import NotFoundScreen from '../components/screens/NotFoundScreen'
import SceneErrorBoundary from '../components/screens/SceneErrorBoundary'
import DesktopWarning from '../components/screens/DesktopWarning'
import LandscapeWarning from '../components/screens/LandscapeWarning'
import {
  preloadTutorialVideos,
  useTutorialVideoPreloadStatus,
} from '../utils/preloadTutorialVideos'
import { StatsOverlay } from '../game/debug/StatsOverlay'

const VALID_PATHNAMES = new Set(['/', '/index.html'])

const App = observer(function App() {
  const currentPhase = store.appFlowPhase
  const isGame = currentPhase === 'run'
  const isSettingsOpen = store.settingsOpen
  const isCacheError = store.cacheStatus === 'error'
  const isCacheReady = store.cacheStatus === 'success'
  const threeDAssetPreload = useThreeDAssetPreload(true)
  const ktx2Preload = useKtx2PreloadStatus()
  const soundPreload = useSoundPreloadStatus()
  const tutorialVideoPreload = useTutorialVideoPreloadStatus()
  const [sceneReady, setSceneReady] = useState(false)

  const cardIdQuery = useCardIdQuery()
  const isParentsLanding = cardIdQuery.isPresent

  const svidQuery = useSvidQuery()
  const characterIdQuery = useCharacterIdQuery()
  const debuggingData = store.getCachedQuery(GetDebuggingDocument)
  const showDebugMenu = debuggingData?.debugging?.showDebugMenue ?? false
  const pathname =
    typeof window === 'undefined' ? '/' : window.location.pathname
  const isNotFoundPath = !VALID_PATHNAMES.has(pathname)

  useEffect(() => {
    applySteleDeviceAttribute(svidQuery.isPresent)
  }, [svidQuery.isPresent])

  useDecodedCardSharePayload(cardIdQuery)
  useDecodedCharacterSharePayload(characterIdQuery)
  useSoundSystem()
  useIntroFlowMusic()

  useEffect(() => {
    if (store.cacheStatus === 'idle') {
      void store.initializeCache()
    }
  }, [])

  useEffect(() => {
    if (store.cacheStatus === 'success') {
      store.recomputeEntryReferrerLandingMapping()
    }
  }, [isCacheReady])

  useEffect(() => {
    if (currentPhase === 'splash') {
      setSceneReady(false)
    }
  }, [currentPhase])

  const assetsReady =
    threeDAssetPreload.ready &&
    ktx2Preload.ready &&
    soundPreload.ready &&
    tutorialVideoPreload.ready
  const canContinueFromSplash = assetsReady && isCacheReady && sceneReady
  const failedAssets = useMemo(
    () => [...threeDAssetPreload.failures, ...ktx2Preload.failures],
    [threeDAssetPreload.failures, ktx2Preload.failures],
  )
  // An asset that ran out of retries would otherwise leave the bar sitting at
  // 100 % forever, so it offers the same retry as a failed cache
  const isLoadingError = isCacheError || failedAssets.length > 0

  const totalLoadingUnits =
    threeDAssetPreload.total +
    ktx2Preload.total +
    soundPreload.total +
    tutorialVideoPreload.total +
    1
  const loadedLoadingUnits =
    threeDAssetPreload.loaded +
    ktx2Preload.loaded +
    soundPreload.loaded +
    tutorialVideoPreload.loaded +
    (isCacheReady ? 1 : 0)
  const progress =
    totalLoadingUnits === 0
      ? 100
      : (loadedLoadingUnits / totalLoadingUnits) * 100

  const splashDebugStatus = useMemo(() => ({
    threeD: `${threeDAssetPreload.loaded}/${threeDAssetPreload.total}${threeDAssetPreload.failed > 0 ? ` ${threeDAssetPreload.failed} failed` : threeDAssetPreload.ready ? ' ok' : ''}`,
    ktx2: `${ktx2Preload.loaded}/${ktx2Preload.total}${ktx2Preload.failed > 0 ? ` ${ktx2Preload.failed} failed` : ktx2Preload.ready ? ' ok' : ''}`,
    audio: `${soundPreload.loaded}/${soundPreload.total}${soundPreload.ready ? ' ok' : ''}`,
    video: `${tutorialVideoPreload.loaded}/${tutorialVideoPreload.total}${tutorialVideoPreload.ready ? ' ok' : ''}`,
    cache: isCacheReady ? 'ok' : store.cacheStatus,
    scene: sceneReady ? 'ok' : 'wait',
    // Which asset gave up and why, readable without a console
    failures: failedAssets
      .map(({ label, message }) => `${label}: ${message}`)
      .join(' | '),
  }), [
    failedAssets,
    isCacheReady,
    ktx2Preload.failed,
    ktx2Preload.loaded,
    ktx2Preload.ready,
    ktx2Preload.total,
    sceneReady,
    soundPreload.loaded,
    soundPreload.ready,
    soundPreload.total,
    tutorialVideoPreload.loaded,
    tutorialVideoPreload.ready,
    tutorialVideoPreload.total,
    threeDAssetPreload.failed,
    threeDAssetPreload.loaded,
    threeDAssetPreload.ready,
    threeDAssetPreload.total,
  ])

  const isStele = useSteleBreakpoint()
  const isTouchDevice = useIsTouchDevice()

  useEffect(() => {
    if (!threeDAssetPreload.ready) return
    preloadTutorialVideos(isStele)
  }, [isStele, threeDAssetPreload.ready])

  const dispatchFlow = useCallback(
    (event: AppFlowEvent) => {
      store.dispatchAppFlow(event, { canContinueFromSplash })
    },
    [canContinueFromSplash],
  )

  const onSplashComplete = useCallback(() => {
    dispatchFlow({ type: 'SPLASH_CONTINUE' })
  }, [dispatchFlow])

  const onRetryLoading = useCallback(() => {
    if (store.cacheStatus === 'error') {
      store.retryCacheInitialization()
    }
    retryFailedThreeDAssets()
    retryFailedKtx2Textures()
  }, [])

  const onAdvanceFlow = useCallback(() => {
    dispatchFlow({ type: 'FLOW_ADVANCE' })
  }, [dispatchFlow])

  const onReturnToStart = useCallback(() => {
    dispatchFlow({ type: 'FLOW_GO_TO_START' })
  }, [dispatchFlow])

  const overlayScreen = useMemo(() => {
    if (isParentsLanding) {
      return (
        <CardLandingScreen
          key='parents'
          cardIdQuery={cardIdQuery}
          assetProgress={progress}
          isLoadingError={isLoadingError}
          onRetryLoading={onRetryLoading}
        />
      )
    }

    if (currentPhase === 'splash') {
      return (
        <motion.div
          key='splash'
          className='z-50 absolute inset-0'
          // Animating a blur across the full viewport costs several frames per
          // update on phones, and `AnimatePresence mode='wait'` holds the next
          // screen back until the exit finished
          exit={isStele
            ? { scale: 1.15, opacity: 0, filter: 'blur(8px)' }
            : { scale: 1.08, opacity: 0 }}
          transition={isStele
            ? { type: 'spring', stiffness: 260, damping: 18, mass: 0.8 }
            : { duration: 0.3, ease: 'easeOut' }}
        >
          <SplashScreen
            assetProgress={progress}
            loadingReady={canContinueFromSplash}
            isLoadingError={isLoadingError}
            onRetryLoading={onRetryLoading}
            onComplete={onSplashComplete}
            debugStatus={showDebugMenu ? splashDebugStatus : undefined}
          />
        </motion.div>
      )
    }

    if (isGame) {
      return null
    }

    if (currentPhase === 'start') {
      return <StartScreen onNext={onAdvanceFlow} />
    }

    if (currentPhase === 'intro') {
      return <IntroScreen key='intro' onNext={onAdvanceFlow}
        onReturnToConfigurator={() =>
          dispatchFlow({ type: 'RETURN_TO_MENU_CONFIGURATOR' })
        } />
    }

    if (currentPhase === 'configurator') {
      return (
        <ConfiguratorScreen
          key='configurator'
          onNext={onAdvanceFlow}
          onBack={onReturnToStart}
        />
      )
    }

    if (currentPhase === 'countdown') {
      return (
        <CountdownScreen
          key='countdown'
          onDone={() => dispatchFlow({ type: 'COUNTDOWN_COMPLETE' })}
        />
      )
    }

    return null
  }, [
    canContinueFromSplash,
    cardIdQuery,
    currentPhase,
    dispatchFlow,
    isGame,
    isLoadingError,
    isParentsLanding,
    isStele,
    onAdvanceFlow,
    onRetryLoading,
    onReturnToStart,
    onSplashComplete,
    progress,
    showDebugMenu,
    splashDebugStatus,
  ])


  if (isNotFoundPath) {
    return (
      <div className='relative bg-slate-500 w-svw h-svh overflow-hidden'>
        <NotFoundScreen />
      </div>
    )
  }

  return (
    <div className='relative bg-slate-500 w-svw h-svh overflow-hidden'>
      <SceneErrorBoundary>
        <Game
          renderEnabled={!isStele || currentPhase !== 'intro'}
          onSceneReady={() => setSceneReady(true)}
          onReturnToMenu={() => dispatchFlow({ type: 'RETURN_TO_MENU' })}
          onReturnToConfigurator={() =>
            dispatchFlow({ type: 'RETURN_TO_MENU_CONFIGURATOR' })
          }
        />
      </SceneErrorBoundary>

      <AnimatePresence mode='wait'>
        {overlayScreen}
      </AnimatePresence>

      <AnimatePresence>
        {isSettingsOpen ? <SettingsScreen /> : null}
      </AnimatePresence>

      <AnimatePresence>
        {store.resetTransitionActive ? <ResetTransitionScreen /> : null}
      </AnimatePresence>

      <InactivityGuard />
      <StatsOverlay />

      {!isStele && (
        isTouchDevice ? (
          <LandscapeWarning />
        ) : (
          <div className='z-999999 fixed inset-0'>
            <DesktopWarning />
          </div>
        )
      )}
    </div>
  )
})

export default App
